import { useState, useCallback, useEffect, useMemo } from 'react';
import { api } from '../api.js';
import { useNotifyStore } from '../store/notifyStore.js';
import {
  DownloadIcon,
  TrashIcon,
  ClockIcon,
  RefreshIcon,
  VideoIcon,
  SearchIcon,
  CheckIcon,
} from '../components/Icons.jsx';
import EmptyState from '../components/EmptyState.jsx';

/** @param {number} seconds */
function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** @param {string} isoStr */
function formatDateTime(isoStr) {
  try {
    const d = new Date(isoStr);
    return d.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoStr;
  }
}

/**
 * @param {object} props
 * @param {import('../api.js').Recording} props.recording
 * @param {(id: string) => void} props.onDelete
 * @param {(rec: import('../api.js').Recording) => void} props.onPlay
 */
function RecordingCard({ recording, onDelete, onPlay }) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const statusLabel = recording.status === 'ready' ? 'Готова' : recording.status === 'processing' ? 'Обработка…' : 'Ошибка';
  const statusBadge = recording.status === 'ready'
    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : recording.status === 'processing'
    ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-rose-50 text-rose-700 border-rose-200';

  const s3Url = recording.s3Url || recording.s3_url || '';
  const s3Key = recording.s3Key || recording.s3_key || '';
  const fileName = s3Key.split('/').pop() || recording.id;
  const isVideo = recording.status === 'ready' && !!s3Url;
  const duration = recording.durationSeconds ?? recording.duration_seconds ?? 0;
  const size = recording.sizeBytes ?? recording.size_bytes ?? 0;
  const createdAt = recording.createdAt ?? recording.created_at ?? '';

  return (
    <div className="group relative rounded-2xl bg-white border border-slate-200/90 hover:border-[#002882]/40 p-5 flex flex-col justify-between gap-4 transition-all duration-200 shadow-sm hover:shadow-lg">
      <div>
        <div className="flex items-start justify-between gap-3 mb-3">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusBadge}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${recording.status === 'ready' ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`} />
            {statusLabel}
          </span>

          <span className="text-xs font-mono uppercase px-2 py-0.5 rounded-md bg-slate-100 border border-slate-200 text-slate-700">
            {recording.format?.toUpperCase() || 'MP4'}
          </span>
        </div>

        <h4 className="text-sm font-bold text-slate-900 truncate group-hover:text-[#002882] transition-colors" title={fileName}>
          {fileName}
        </h4>

        {recording.roomId && (
          <p className="text-xs text-slate-500 font-mono mt-1 truncate">
            Комната: {recording.roomId}
          </p>
        )}
      </div>

      {/* Meta Specs */}
      <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-100 text-xs text-slate-500">
        <div className="flex items-center gap-1.5">
          <ClockIcon size={14} className="text-[#002882]" />
          <span className="font-medium text-slate-700">{formatDuration(duration)}</span>
        </div>
        <div className="text-right font-medium text-slate-700">
          {size ? `${(size / 1048576).toFixed(1)} МБ` : '—'}
        </div>
        <div className="col-span-2 text-[11px] text-slate-400">
          {formatDateTime(createdAt)}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
        {isVideo && (
          <button
            type="button"
            onClick={() => onPlay(recording)}
            className="flex-1 py-2 px-3 rounded-xl bg-[#f0f4fc] hover:bg-[#e2ecfc] text-[#002882] border border-[#d1dcf0] text-xs font-semibold flex items-center justify-center gap-1.5 transition-all active:scale-[0.98]"
          >
            <VideoIcon size={14} />
            <span>Смотреть</span>
          </button>
        )}

        {isVideo && (
          <a
            href={s3Url}
            download
            title="Скачать файл"
            className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-[#002882] border border-slate-200 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
          >
            <DownloadIcon size={15} />
          </a>
        )}

        {confirmDelete ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onDelete(recording.id)}
              className="py-1.5 px-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition-colors"
            >
              Да
            </button>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="py-1.5 px-2 rounded-lg bg-slate-100 text-slate-700 text-xs"
            >
              Нет
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            title="Удалить запись"
            className="p-2 rounded-xl bg-slate-100 hover:bg-red-50 text-slate-400 hover:text-red-600 border border-slate-200 transition-all active:scale-95"
          >
            <TrashIcon size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function Recordings() {
  const notify = useNotifyStore((s) => s.notify);
  const [recordings, setRecordings] = useState(/** @type {import('../api.js').Recording[]} */ ([]));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeVideo, setActiveVideo] = useState(/** @type {import('../api.js').Recording|null} */ (null));

  const load = useCallback(async () => {
    try {
      const data = await api.listRecordings();
      setRecordings(data ?? []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = useCallback(async (id) => {
    try {
      await api.deleteRecording(id);
      notify('success', 'Запись удалена');
      await load();
    } catch (err) {
      notify('error', `Не удалось удалить запись: ${err.message}`);
    }
  }, [notify, load]);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return recordings;
    const q = searchQuery.toLowerCase();
    return recordings.filter((r) => {
      const name = (r.s3Key || '').toLowerCase();
      const id = (r.id || '').toLowerCase();
      const room = (r.roomId || '').toLowerCase();
      return name.includes(q) || id.includes(q) || room.includes(q);
    });
  }, [recordings, searchQuery]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-8 animate-fade-in">
      {/* ── Hero Banner (Sovcombank Branding) ── */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white via-[#f0f4fc]/80 to-[#e4edf9]/90 border border-[#d1dcf0] p-6 sm:p-10 shadow-sm backdrop-blur-xl">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#002882]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#ea1c24]/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-xl space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white text-[#002882] text-xs font-semibold border border-[#d1dcf0] shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              Хранилище видеозаписей S3 / MinIO
            </div>
            <h1 className="text-2xl sm:text-4xl font-black text-[#002882] tracking-tight leading-tight">
              Записи консультаций
            </h1>
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
              Все записанные видеосессии клиентов и операторов сохраняются в защищенном хранилище с возможностью предпросмотра и скачивания.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={load}
              className="py-3 px-5 rounded-2xl bg-white hover:bg-slate-50 text-[#002882] font-semibold text-sm flex items-center gap-2 border border-slate-200 shadow-sm active:scale-95 transition-all"
            >
              <RefreshIcon size={16} className={loading ? 'animate-spin' : ''} />
              <span>Обновить</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Search Bar ── */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <SearchIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Поиск записи по комнате или названию файла…"
            className="w-full pl-11 pr-4 py-3 rounded-2xl bg-white border border-slate-200 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-[#002882] focus:ring-2 focus:ring-[#002882]/10 transition-all shadow-sm"
          />
        </div>
      </div>

      {/* ── Content Grid ── */}
      {loading ? (
        <div className="py-20 text-center space-y-3">
          <div className="w-10 h-10 mx-auto rounded-full border-2 border-[#002882]/20 border-t-[#002882] animate-spin" />
          <p className="text-sm text-slate-500">Загрузка библиотеки записей…</p>
        </div>
      ) : error ? (
        <div className="p-6 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm text-center shadow-sm">
          {error}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={searchQuery ? 'Ничего не найдено' : 'Записей пока нет'}
          description={
            searchQuery
              ? 'Попробуйте изменить запрос для поиска.'
              : 'Записи звонков появятся здесь автоматически после завершения и сохранения консультации.'
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 animate-fade-in">
          {filtered.map((r) => (
            <RecordingCard key={r.id} recording={r} onDelete={handleDelete} onPlay={setActiveVideo} />
          ))}
        </div>
      )}

      {/* ── Video Player Modal ── */}
      {activeVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
          <div className="relative w-full max-w-4xl bg-white rounded-3xl overflow-hidden shadow-2xl flex flex-col border border-slate-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 px-6 border-b border-slate-100 bg-[#f8fafc]">
              <div className="min-w-0 pr-4">
                <h3 className="text-sm sm:text-base font-bold text-slate-900 truncate">
                  {(activeVideo.s3Key || '').split('/').pop() || activeVideo.id}
                </h3>
                <p className="text-xs text-slate-500">
                  {formatDateTime(activeVideo.createdAt)} · {formatDuration(activeVideo.durationSeconds)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActiveVideo(null)}
                className="py-1.5 px-3 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 transition-all text-xs font-bold"
              >
                ✕ Закрыть
              </button>
            </div>

            {/* Video Player */}
            <div className="bg-black flex items-center justify-center aspect-video max-h-[70vh]">
              <video
                src={activeVideo.s3Url || activeVideo.s3_url}
                controls
                autoPlay
                playsInline
                className="w-full h-full object-contain"
              />
            </div>

            {/* Modal Footer */}
            <div className="p-4 px-6 flex items-center justify-between bg-[#f8fafc] border-t border-slate-100">
              <span className="text-xs text-slate-500">
                Формат: {activeVideo.format?.toUpperCase() || 'MP4'} · Размер: {(((activeVideo.sizeBytes ?? activeVideo.size_bytes) || 0) / 1048576).toFixed(1)} МБ
              </span>
              <a
                href={activeVideo.s3Url || activeVideo.s3_url}
                download
                className="py-2.5 px-5 rounded-xl bg-[#002882] hover:bg-[#001f66] text-white text-xs font-semibold flex items-center gap-1.5 transition-all active:scale-95 shadow-md shadow-[#002882]/20"
              >
                <DownloadIcon size={14} />
                <span>Скачать видео</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}